// Навигация в сайдбаре, зависит от роли. Структура согласована с TZ.md §3 и §16.
// Добавление новых пунктов — только тут (один источник правды).

import { NavLink as MantineNavLink, Stack } from '@mantine/core';
import {
    IconBuildingSkyscraper,
    IconUsers,
    IconChartBar,
    IconBrandHipchat,
    IconHistory,
    IconFileText,
    IconAdjustments,
    IconUserCircle,
} from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';

const NAV_BY_ROLE = {
    creator: [
        { to: '/tenants', label: 'Клиенты', icon: IconBuildingSkyscraper },
        { to: '/users', label: 'Пользователи', icon: IconUsers },
        { to: '/prompts', label: 'Промпты', icon: IconFileText },
        { to: '/settings', label: 'Настройки', icon: IconAdjustments },
        { to: '/trainer', label: 'Тренажёр', icon: IconBrandHipchat },
        { to: '/my-trainings', label: 'Мои тренировки', icon: IconHistory },
        { to: '/dialogs', label: 'Диалоги', icon: IconBrandHipchat },
    ],
    // Куратор — помощник Создателя: только клиенты и пользователи (тарифы ставит
    // через карточку клиента). Без промптов/настроек/«Войти как».
    curator: [
        { to: '/tenants', label: 'Клиенты', icon: IconBuildingSkyscraper },
        { to: '/users', label: 'Пользователи', icon: IconUsers },
    ],
    admin: [
        { to: '/users', label: 'Пользователи', icon: IconUsers },
        { to: '/analytics', label: 'Аналитика', icon: IconChartBar },
        { to: '/trainer', label: 'Тренажёр', icon: IconBrandHipchat },
        { to: '/my-trainings', label: 'Мои тренировки', icon: IconHistory },
        { to: '/dialogs', label: 'Диалоги сотрудников', icon: IconBrandHipchat },
    ],
    rop: [
        { to: '/users', label: 'Мои МОПы', icon: IconUsers },
        { to: '/analytics', label: 'Аналитика', icon: IconChartBar },
        { to: '/trainer', label: 'Тренажёр', icon: IconBrandHipchat },
        { to: '/my-trainings', label: 'Мои тренировки', icon: IconHistory },
        { to: '/dialogs', label: 'Диалоги сотрудников', icon: IconBrandHipchat },
    ],
    mop: [
        { to: '/trainer', label: 'Тренажёр', icon: IconBrandHipchat },
        { to: '/dialogs', label: 'Мои тренировки', icon: IconHistory },
    ],
};

// Пункт «Мой аккаунт» — ведёт на /users/<self.id>. Здесь МОП/РОП/Админ
// привязывают Telegram, видят свой ИНН, могут поменять пароль.
// Креатор тоже видит — там же кнопка «Сменить пароль» и его профиль.
function selfAccountItem(user) {
    return { to: `/users/${user.id}`, label: 'Мой аккаунт', icon: IconUserCircle };
}

function isActive(currentPath, target, { exact = false } = {}) {
    if (target === '/') return currentPath === '/';
    if (exact) return currentPath === target;
    return currentPath === target || currentPath.startsWith(target + '/');
}

export function RoleSidebar() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    if (!user) return null;
    const baseItems = NAV_BY_ROLE[user.role] || [];
    // «Мой аккаунт» — последний пункт у всех ролей.
    const items = [...baseItems, selfAccountItem(user)];
    const selfAccountPath = `/users/${user.id}`;

    return (
        <Stack gap={4}>
            {items.map(({ to, label, icon: Icon }) => {
                // «Мой аккаунт» — exact match. Иначе если юзер на /users/12,
                // пункт «Пользователи» (/users) тоже подсветился бы из-за startsWith.
                const isSelfAccount = to === selfAccountPath;
                let active;
                if (isSelfAccount) {
                    active = pathname === selfAccountPath;
                } else if (to === '/users') {
                    // «Пользователи / Мои МОПы» — active на /users и на чужих /users/<id>,
                    // но не на собственном /users/<self.id> (там подсветится «Мой аккаунт»).
                    active = (pathname === '/users' || pathname.startsWith('/users/'))
                        && pathname !== selfAccountPath;
                } else {
                    active = isActive(pathname, to);
                }
                return (
                    <MantineNavLink
                        key={to}
                        component={Link}
                        to={to}
                        label={label}
                        leftSection={<Icon size={18} stroke={1.75} />}
                        active={active}
                        variant="filled"
                    />
                );
            })}
        </Stack>
    );
}
